import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircle2Icon } from 'lucide-react';
import { scanPageProducts, DetectedProduct } from '../utils/productScanner';
import { generateFakePurchase, PurchaseNotification } from '../utils/recentPurchaseData';
function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return prefersReduced;
}
const TOAST_VISIBLE_MS = 4000;
const FIRST_DELAY_MS = 3000;
const INTERVAL_MIN_MS = 8000;
const INTERVAL_MAX_MS = 15000;
export function RecentPurchaseToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [notification, setNotification] = useState<PurchaseNotification | null>(null);
  const [products, setProducts] = useState<DetectedProduct[]>([]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const cycleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Initialize products on mount
  useEffect(() => {
    const scanned = scanPageProducts();
    setProducts(scanned);
  }, []);
  const showNextNotification = useCallback(() => {
    if (products.length === 0) return;
    const purchase = generateFakePurchase(products);
    setNotification(purchase);
    setIsVisible(true);
    // Auto-hide after TOAST_VISIBLE_MS
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, TOAST_VISIBLE_MS);
  }, [products]);
  useEffect(() => {
    if (products.length === 0) return;
    const scheduleNext = () => {
      const delay = Math.floor(Math.random() * (INTERVAL_MAX_MS - INTERVAL_MIN_MS + 1) + INTERVAL_MIN_MS);
      cycleTimerRef.current = setTimeout(() => {
        showNextNotification();
        scheduleNext();
      }, delay);
    };
    // First notification after 3 seconds
    const initialTimeout = setTimeout(() => {
      showNextNotification();
      scheduleNext();
    }, FIRST_DELAY_MS);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(cycleTimerRef.current);
      clearTimeout(hideTimerRef.current);
    };
  }, [products, showNextNotification]);
  const motionVariants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 40
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 40,
      transition: {
        duration: 0.3
      }
    }
  };
  const motionTransition = prefersReducedMotion ? {
    duration: 0.15
  } : {
    type: 'spring' as const,
    stiffness: 350,
    damping: 28
  };
  const priceDisplay = notification?.product.priceText || (notification ? `$${notification.product.priceNumber}` : '');
  return <AnimatePresence data-id="element-237">
      {isVisible && notification && <motion.div key={notification.buyerName + notification.city} {...motionVariants} transition={motionTransition} className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-[360px] bg-[#111827] border border-[#d4a853]/30 rounded-lg shadow-2xl overflow-hidden" data-id="element-238">
          <div className="relative p-4 flex items-start space-x-3" data-id="element-239">
            {/* Close button */}
            <button onClick={() => setIsVisible(false)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-300 transition-colors" aria-label="Close notification" data-id="element-240">
              <XIcon className="w-3.5 h-3.5" data-id="element-241" />
            </button>

            {/* Product thumbnail */}
            <div className="flex-shrink-0 mt-0.5" data-id="element-242">
              {notification.product.thumbnailUrl ? <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-11 h-11 object-cover rounded-md border border-[#d4a853]/20" data-id="element-243" /> : <div className="w-11 h-11 bg-[#d4a853]/10 rounded-md border border-[#d4a853]/20 flex items-center justify-center" data-id="element-244">
                  <CheckCircle2Icon className="w-5 h-5 text-[#d4a853]" data-id="element-245" />
                </div>}
            </div>

            {/* Notification content */}
            <div className="flex-1 min-w-0 pr-5" data-id="element-246">
              <p className="font-sans text-[13px] text-gray-100 leading-snug" data-id="element-247">
                <span className="font-medium" data-id="element-248">{notification.buyerName}</span> in{' '}
                <span className="text-gray-400" data-id="element-249">{notification.city}</span> just
                purchased
              </p>
              <p className="font-sans text-[13px] text-gray-300 mt-0.5" data-id="element-250">
                <span className="font-medium text-gray-100" data-id="element-251">
                  {notification.product.editionLabel || notification.product.title}
                </span>
                {' — '}
                <span className="font-mono text-xs font-bold text-[#d4a853]" data-id="element-252">
                  {priceDisplay}
                </span>
              </p>
            </div>
          </div>

          {/* Progress bar */}
          {!prefersReducedMotion && <motion.div initial={{
        width: '100%'
      }} animate={{
        width: '0%'
      }} transition={{
        duration: TOAST_VISIBLE_MS / 1000,
        ease: 'linear'
      }} className="h-[2px] bg-[#d4a853]/50" data-id="element-253" />}
        </motion.div>}
    </AnimatePresence>;
}